import gql from 'graphql-tag';
import apolloClient from '../apollo';

/**
 * State
 */
const defaultClient = () => {
  return {
    id: '_new',
    number: '_new',
    name: '',
    phone: '',
    email: '',
    website: '',
    attachments: [],
  };
};

const getDefaultState = () => {
  return {
    clients: [],
    clientCount: 0,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for an client
 * used for all queries/mutations which return an client
 * @type {DocumentNode}
 */
const CLIENT_REQUEST_FIELDS = gql`
  fragment clientFields on Client {
    id
    number
    name
    street
    zip
    city
    phone
    email
    website
    description
    type
    smtpHost
    smtpUser
    smtpPort
    smtpPass
    mailFrom
    mailBccTo
    mailReplyTo
    emailSignature
    attachmentTypes
    attachments {
      id
      path
      filename
      displayFilename
      size
      mimetype
      attachmentType
    }
    electricians {
      name
      id
      number
    }
    archiveState
    roleNavigationEnabled
  }
`;

/**
 * Fields for client list queries. Omits attachments because paginated aggregation
 * does not populate them (GraphQL ID cannot serialize raw ObjectId buffers).
 * @type {DocumentNode}
 */
const CLIENT_LIST_REQUEST_FIELDS = gql`
  fragment clientListFields on Client {
    id
    number
    name
    street
    zip
    city
    archiveState
    roleNavigationEnabled
  }
`;

/**
 * sanitize inputs and create payload to send to the graphql api
 * @param {*} client
 */
const createPayload = (client) => {
  /** Only send ID's for electricians */
  if (client.electricians) {
    const ids = [];
    client.electricians.map((electrician) => {
      ids.push(electrician.id);
    });
    client.electricians = ids;
  }
  /** Only send ID's for employers */
  if (client.employers) {
    const ids = [];
    client.employers.map((employer) => {
      ids.push(employer.id);
    });
    client.employers = ids;
  }

  const payload = {
    ...(client.name ? { name: client.name } : {}),
    ...(client.street ? { street: client.street } : {}),
    ...(client.zip !== null ? { zip: client.zip } : {}),
    ...(client.city !== null ? { city: client.city } : {}),
    ...(client.phone !== null ? { phone: client.phone } : {}),
    ...(client.email !== null ? { email: client.email } : {}),
    ...(client.website !== null ? { website: client.website } : {}),
    ...(client.description !== null ? { description: client.description } : {}),
    ...(client.smtpHost !== null ? { smtpHost: client.smtpHost } : {}),
    ...(client.smtpUser !== null ? { smtpUser: client.smtpUser } : {}),
    ...(client.smtpPort !== null ? { smtpPort: client.smtpPort } : {}),
    ...(client.smtpPass !== null ? { smtpPass: client.smtpPass } : {}),
    ...(client.mailFrom !== null ? { mailFrom: client.mailFrom } : {}),
    ...(client.mailBccTo !== null ? { mailBccTo: client.mailBccTo } : {}),
    ...(client.mailReplyTo !== null ? { mailReplyTo: client.mailReplyTo } : {}),
    ...(client.emailSignature !== null ? { emailSignature: client.emailSignature } : {}),
    electricians: client.electricians,
    employers: client.employers,
    archiveState: client.archiveState,
    ...(typeof client.roleNavigationEnabled === 'boolean'
      ? { roleNavigationEnabled: client.roleNavigationEnabled }
      : {}),
  };
  return payload;
};

/** The organization of a user is the first client, as in the backend (OrganizationService). */
const isOwnOrganization = (currentUser, client) => {
  const organization = currentUser && currentUser.clients && currentUser.clients[0];
  return Boolean(organization) && organization.id === client.id;
};

/**
 * Actions
 */
const actions = {
  /**
   * Fetch a paginated, sorted and filtered client list
   * @param {*} param0
   * @param {*} payload
   */
  async fetchClientsPaginated({ commit, dispatch }, { pagination, sorting, filters }) {
    // console.log('fetchClientsPaginated()', pagination, sorting, filters);
    try {
      const response = await apolloClient.query({
        query: gql`
          query clientsPaginated(
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: ClientFilterInput
          ) {
            clientsPaginated(pagination: $pagination, sorting: $sorting, filters: $filters) {
              clients {
                ...clientListFields
              }
              clientCount
            }
          }
          ${CLIENT_LIST_REQUEST_FIELDS}
        `,
        variables: {
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });

      commit('SET_CLIENTS', response.data.clientsPaginated.clients);
      commit('SET_CLIENT_COUNT', response.data.clientsPaginated.clientCount);
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  async fetchClients({ commit, dispatch }, payload) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            clients {
              ...clientListFields
            }
          }
          ${CLIENT_LIST_REQUEST_FIELDS}
        `,
      });
      commit('SET_CLIENTS', response.data.clients);
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchClientByNumber({ commit, dispatch }, clientNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query clientByNumber($clientNumber: Int!) {
            clientByNumber(clientNumber: $clientNumber) {
              ...clientFields
            }
          }
          ${CLIENT_REQUEST_FIELDS}
        `,
        variables: {
          clientNumber: parseInt(clientNumber),
        },
      });
      commit('ADD_OR_UPDATE_CLIENT', response.data.clientByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async createClient({ commit, dispatch }, client) {
    const payload = createPayload(client);

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation createClient($payload: ClientInput!) {
            createClient(clientInput: $payload) {
              ...clientFields
            }
          }
          ${CLIENT_REQUEST_FIELDS}
        `,
        variables: {
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Auftraggeber gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_CLIENT', '_new'); // remove temporary _new client
      commit('ADD_OR_UPDATE_CLIENT', response.data.createClient);
      return response.data.createClient.number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /** Saving the own organization refreshes the current user, so menu and access follow. */
  async updateClient({ commit, dispatch, rootGetters }, client) {
    const payload = createPayload(client);

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateClient($clientNumber: Int!, $payload: ClientInput!) {
            updateClient(clientNumber: $clientNumber, clientInput: $payload) {
              ...clientFields
            }
          }
          ${CLIENT_REQUEST_FIELDS}
        `,
        variables: {
          clientNumber: client.number,
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Auftraggeber aktualisiert!',
        type: 'success',
        lifetime: 2,
      });
      commit('ADD_OR_UPDATE_CLIENT', response.data.updateClient);
      if (isOwnOrganization(rootGetters['auth/getCurrentUser'], response.data.updateClient)) {
        await dispatch('auth/refreshUser', null, { root: true });
      }
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim bearbeiten! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async deleteClient({ commit, dispatch }, clientNumber) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deleteClient($clientNumber: Int!) {
            deleteClient(clientNumber: $clientNumber)
          }
        `,
        variables: {
          clientNumber: clientNumber,
        },
      });
      dispatch('message', {
        message: 'Auftraggeber gelöscht!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_CLIENT', clientNumber);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async uploadClientAttachment({ commit, dispatch }, payload) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation uploadClientAttachment($attachmentInput: AttachmentWithTypeInput) {
            uploadClientAttachment(attachmentInput: $attachmentInput) {
              ...clientFields
            }
          }
          ${CLIENT_REQUEST_FIELDS}
        `,
        variables: {
          attachmentInput: {
            referenceId: payload.parentId,
            referenceType: payload.parentType,
            file: payload.file,
          },
        },
      });
      commit('ADD_OR_UPDATE_CLIENT', response.data.uploadClientAttachment);
      dispatch('message', {
        message: 'Anhang hochgeladen!',
        type: 'success',
        lifetime: 2,
      });
      return response.data.uploadClientAttachment;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim hochladen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async deleteClientAttachment({ commit, dispatch }, { clientId, attachmentId }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deleteClientAttachment($clientId: ID!, $attachmentId: ID!) {
            deleteClientAttachment(clientId: $clientId, attachmentId: $attachmentId) {
              ...clientFields
            }
          }
          ${CLIENT_REQUEST_FIELDS}
        `,
        variables: {
          clientId,
          attachmentId,
        },
      });
      commit('ADD_OR_UPDATE_CLIENT', response.data.deleteClientAttachment);
      dispatch('message', {
        message: 'Anhang gelöscht!',
        type: 'success',
        lifetime: 2,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * create a new client object in store and return it
   * @param {*} param0
   * @param {*} client
   */
  async initClient({ commit }) {
    commit('ADD_OR_UPDATE_CLIENT', defaultClient());
  },

  clientLocalUpdate({ commit }, client) {
    commit('ADD_OR_UPDATE_CLIENT', client);
  },

  clientCleanup({ commit }) {
    commit('REMOVE_CLIENT', '_new');
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_CLIENT_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_CLIENTS: (state, clients) => (clients ? (state.clients = clients) : (state.clients = [])),
  ADD_CLIENT: (state, client) => state.clients.push(client),
  ADD_OR_UPDATE_CLIENT: (state, updatedClient) => {
    const existingClient = state.clients.find((client) => client.id === updatedClient.id);
    if (existingClient) {
      Object.assign(existingClient, updatedClient);
    } else {
      state.clients.push(updatedClient);
    }
  },
  REMOVE_CLIENT: (state, clientId) => {
    const index = state.clients.findIndex((client) => client.id === clientId);
    state.clients.splice(index, 1);
  },
  SET_CLIENT_COUNT: (state, clientCount) => (state.clientCount = clientCount),
};

/**
 * Getters
 */
const getters = {
  getClients: (state) => state.clients,
  getClient: (state) => (clientId) => state.clients.find((client) => client.id == clientId),
  getClientByNumber: (state) => (clientNumber) =>
    state.clients.find((client) => client.number == clientNumber),
  getClientCount: (state) => state.clientCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
